import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { fallbackSrc, srcset, type Photo } from "@/lib/photos";

type LightboxProps = {
  photos: Photo[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;

export function Lightbox({ photos, index, onClose, onIndexChange }: LightboxProps) {
  const open = index !== null;
  const [visible, setVisible] = useState(false);

  // zoom state
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTap = useRef({ time: 0, x: 0, y: 0 });

  const resetZoom = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    pinchStart.current = null;
    panStart.current = null;
    pointers.current.clear();
  }, []);

  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      onIndexChange((index + delta + photos.length) % photos.length);
    },
    [index, photos.length, onIndexChange],
  );

  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
  }, [open]);

  // reset zoom whenever the displayed photo changes
  useEffect(() => {
    resetZoom();
  }, [index, resetZoom]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "0") resetZoom();
    };

    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, go, onClose, resetZoom]);

  /** Keeps the zoomed image from being dragged off-stage. */
  const clamp = useCallback((nextScale: number, nextTx: number, nextTy: number) => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return { scale: nextScale, tx: nextTx, ty: nextTy };

    // offsetWidth/Height are the untransformed layout size
    const baseW = img.offsetWidth;
    const baseH = img.offsetHeight;
    if (!baseW || !baseH) return { scale: nextScale, tx: nextTx, ty: nextTy };

    const maxX = Math.max(0, (baseW * nextScale - stage.clientWidth) / 2);
    const maxY = Math.max(0, (baseH * nextScale - stage.clientHeight) / 2);

    return {
      scale: nextScale,
      tx: Math.min(maxX, Math.max(-maxX, nextTx)),
      ty: Math.min(maxY, Math.max(-maxY, nextTy)),
    };
  }, []);

  const applyZoom = useCallback(
    (nextScale: number, nextTx: number, nextTy: number) => {
      const c = clamp(nextScale, nextTx, nextTy);
      setScale(c.scale);
      setTx(c.tx);
      setTy(c.ty);
    },
    [clamp],
  );

  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  const mid = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      const a = pts[0];
      const b = pts[1];
      if (!a || !b) return;
      pinchStart.current = { dist: dist(a, b), scale };
      panStart.current = null;
      return;
    }

    // double tap to toggle zoom
    const now = Date.now();
    if (
      now - lastTap.current.time < DOUBLE_TAP_MS &&
      Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y) < 32
    ) {
      lastTap.current = { time: 0, x: 0, y: 0 };
      if (scale > 1.05) {
        applyZoom(1, 0, 0);
      } else {
        applyZoom(DOUBLE_TAP_SCALE, 0, 0);
      }
      return;
    }
    lastTap.current = { time: now, x: e.clientX, y: e.clientY };

    if (scale > 1.01) {
      panStart.current = { x: e.clientX, y: e.clientY, tx, ty };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2 && pinchStart.current) {
      const pts = Array.from(pointers.current.values());
      const a = pts[0];
      const b = pts[1];
      if (!a || !b) return;
      const ratio = dist(a, b) / (pinchStart.current.dist || 1);
      const nextScale = Math.min(MAX_SCALE, Math.max(1, pinchStart.current.scale * ratio));

      // zoom toward the pinch midpoint so it feels anchored
      const centre = mid(a, b);
      const stage = stageRef.current;
      if (stage) {
        const rect = stage.getBoundingClientRect();
        const cx = centre.x - (rect.left + rect.width / 2);
        const cy = centre.y - (rect.top + rect.height / 2);
        const k = nextScale / (pinchStart.current.scale || 1);
        applyZoom(nextScale, tx + cx * (k - 1), ty + cy * (k - 1));
      } else {
        applyZoom(nextScale, tx, ty);
      }
      return;
    }

    if (pointers.current.size === 1 && panStart.current) {
      applyZoom(
        scale,
        panStart.current.tx + (e.clientX - panStart.current.x),
        panStart.current.ty + (e.clientY - panStart.current.y),
      );
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;
  };

  // trackpad / mouse wheel zoom
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 2) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    applyZoom(Math.min(MAX_SCALE, Math.max(1, scale * factor)), tx, ty);
  };

  const photo = index === null ? null : photos[index];
  if (!open || !photo) return null;

  const zoomed = scale > 1.01;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      onClick={onClose}
      className={cn(
        "fixed inset-0 z-50 flex flex-col bg-espresso/95 backdrop-blur-sm transition-opacity duration-500",
        visible ? "opacity-100" : "opacity-0",
      )}
    >
      <div className="flex items-center justify-between px-4 pt-4 sm:px-6 sm:pt-6">
        <span className="font-sans text-[0.625rem] tracking-[0.24em] text-ivory/60 uppercase">
          {zoomed ? `${Math.round(scale * 100)}%` : "Pinch to zoom"}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory/10"
        >
          <X className="h-5 w-5" strokeWidth={1.25} />
        </button>
      </div>

      <div
        ref={stageRef}
        onClick={e => e.stopPropagation()}
        className="relative flex min-h-0 flex-1 touch-none items-center justify-center overflow-hidden p-4 sm:p-10"
      >
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous photo"
          className="absolute left-2 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory/10 sm:left-6"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.25} />
        </button>

        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next photo"
          className="absolute right-2 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-ivory/25 text-ivory transition-colors hover:bg-ivory/10 sm:right-6"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={1.25} />
        </button>

        <figure
          onClick={e => e.stopPropagation()}
          className={cn(
            "flex max-h-full max-w-4xl flex-col items-center gap-4 transition-all duration-500",
            visible ? "scale-100 opacity-100" : "scale-[0.97] opacity-0",
          )}
        >
          <img
            key={photo.slug}
            ref={imgRef}
            src={fallbackSrc(photo)}
            srcSet={srcset(photo)}
            sizes="100vw"
            alt={photo.alt}
            width={photo.width}
            height={photo.height}
            draggable={false}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            style={{
              transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
              transformOrigin: "center center",
            }}
            className={cn(
              "max-h-[68vh] w-auto rounded-sm object-contain shadow-2xl",
              zoomed ? "cursor-grab" : "cursor-zoom-in",
              zoomed && "active:cursor-grabbing",
            )}
          />
          <figcaption className="text-center font-sans text-[0.6875rem] tracking-[0.24em] text-ivory/70 uppercase">
            {photo.alt}
          </figcaption>
        </figure>

        {!zoomed && (
          <span className="pointer-events-none absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-ivory/20 px-3 py-1.5 font-sans text-[0.5625rem] tracking-[0.2em] text-ivory/50 uppercase">
            <ZoomIn className="h-3 w-3" strokeWidth={1.5} />
            Double-tap or pinch
          </span>
        )}
      </div>
    </div>
  );
}
